import { Component } from "@theme/component";
import { morph } from "@theme/morph";
import { DialogComponent, DialogCloseEvent } from "@theme/dialog";
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from "@theme/utilities";
import VariantPicker from "@theme/variant-picker";
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from "@shopify/events";
export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = /* @__PURE__ */ new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  get productPageUrl() {
    const productCard = (
      /** @type {import('./product-card').ProductCard | null} */
      this.closest("product-card")
    );
    if (productCard) return productCard.productPageUrl;
    const hotspotProduct = (
      /** @type {import('./product-hotspot').ProductHotspotComponent | null} */
      this.closest("product-hotspot-component")
    );
    const productLink = hotspotProduct?.getHotspotProductLink();
    return productLink?.href || "";
  }
  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = (
      /** @type {import('./product-card').ProductCard | null} */
      this.closest("product-card")
    );
    return productCard?.getSelectedVariantId() ?? null;
  }
  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener("change", this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener("change", this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }
  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest("product-card") !== this.closest("product-card")) return;
    if (this.dataset.usesSellingPlans === "true") return;
    if (this.dataset.rendersBothButtons !== "true") return;
    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === "1" ? "add" : "choose";
    if (quickAddButton === "add" && this.#isSelectedVariantUnavailable()) {
      quickAddButton = "choose";
    }
    this.setAttribute("data-quick-add-button", quickAddButton);
  };
  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = (
      /** @type {import('./product-card').ProductCard | null} */
      this.closest("product-card")
    );
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === "false";
  }
  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };
  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById("quick-add-modal-content");
    if (!modalContent) return;
    const variantPicker = (
      /** @type {VariantPicker | null} */
      modalContent.querySelector("variant-picker")
    );
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }
  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    const currentUrl = this.productPageUrl;
    if (this.dataset.usesSellingPlans === "true") {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }
    let productGrid = this.#cachedContent.get(currentUrl);
    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector("[data-product-grid-content]");
        if (gridElement) {
          productGrid = /** @type {Element} */
          gridElement.cloneNode(true);
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }
    if (productGrid) {
      const freshContent = (
        /** @type {Element} */
        productGrid.cloneNode(true)
      );
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }
    this.#openQuickAddModal();
  };
  #resetScroll() {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    const productDetails = dialogComponent.querySelector(".product-details");
    const productMedia = dialogComponent.querySelector(".product-information__media");
    productDetails?.scrollTo({ top: 0, behavior: "instant" });
    productMedia?.scrollTo({ top: 0, behavior: "instant" });
  }
  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute("stay-visible", true);
    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute("stay-visible", false), {
      once: true
    });
  }
  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener("animationstart", this.#resetScroll.bind(this), { once: true });
  };
  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    dialogComponent.closeDialog();
  };
  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");
      return html;
    } catch (error) {
      if (error.name === "AbortError") {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }
  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById("quick-add-modal-content");
    if (!productGrid || !modalContent) return;
    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector(".product-details");
      const productFormComponent = productGrid.querySelector("product-form-component");
      const variantPicker = productGrid.querySelector("variant-picker");
      const productPrice = productGrid.querySelector("product-price");
      const productTitle = document.createElement("a");
      productTitle.textContent = this.dataset.productTitle || "";
      productTitle.href = this.productPageUrl;
      const productHeader = document.createElement("div");
      productHeader.classList.add("product-header");
      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);
      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      productDetails?.remove();
    }
    const payload = productGrid.getAttribute("view-event-payload") || "";
    modalContent.setAttribute("view-event-payload", payload);
    morph(modalContent, productGrid);
    this.#syncVariantSelection(modalContent);
  }
  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
        break;
      }
    }
  }
}
if (!customElements.get("quick-add-component")) {
  customElements.define("quick-add-component", QuickAddComponent);
}
class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }
  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise?.then(({ detail }) => {
      if (detail?.didError) return;
      this.closeDialog();
    }).catch((error) => {
      if (error?.name !== "AbortError") console.warn("[quick-add] Event promise rejected:", error);
    });
  };
  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    event.promise.then(({ detail }) => {
      if (!detail?.html) return;
      const { html } = detail;
      const anchorElement = (
        /** @type {HTMLAnchorElement} */
        html.querySelector(".view-product-title a")
      );
      const viewMoreDetailsLink = (
        /** @type {HTMLAnchorElement} */
        this.querySelector(".view-product-title a")
      );
      const mobileProductTitle = (
        /** @type {HTMLAnchorElement} */
        this.querySelector(".product-header a")
      );
      if (!anchorElement) return;
      if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
      if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    }).catch((error) => {
      if (error?.name !== "AbortError") console.warn("[quick-add] Event promise rejected:", error);
    });
  };
  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || iosVersion.major === 16 && iosVersion.minor >= 4) return;
    requestAnimationFrame(() => {
      const grid = document.querySelector("#ResultsList [product-grid-view]");
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = "";
        });
      }
    });
  };
}
if (!customElements.get("quick-add-dialog")) {
  customElements.define("quick-add-dialog", QuickAddDialog);
}
